import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bookmark, ArrowRight, Loader2, Trash2, Ban, CheckCircle2 } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import JobCard from '@/components/JobCard';
import { fetchMySavedLeads, SavedLeadRow, useSavedLeads } from '@/lib/savedLeads';
import { localizedSlug } from '@/data/constants';

/**
 * DASHBOARD → SAVED LEADS. Lists the caller's bookmarks (own rows only, via
 * the mkt_my_saved_leads RPC). Every card is the normal JobCard, so opening
 * details and purchasing follow the exact same flow as the feed. Removing a
 * save is optimistic: the card disappears instantly through the shared store.
 * Leads that were unpublished / filled up stay listed as "no longer
 * available" with a one-tap remove, so nothing vanishes silently.
 */
const PAGE = 6;

const SavedLeadsPanel: React.FC<{ credits?: number }> = ({ credits }) => {
  const { lang, path } = useI18n();
  const es = lang === 'es';
  const navigate = useNavigate();
  const { userId, isSaved, toggle, count } = useSavedLeads();
  const [rows, setRows] = useState<SavedLeadRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [shown, setShown] = useState(PAGE);

  useEffect(() => {
    if (!userId) return;
    let alive = true;
    setLoading(true);
    fetchMySavedLeads()
      .then((r) => alive && (setRows(r), setError(false)))
      .catch(() => alive && setError(true))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [userId]);

  // Rows removed anywhere (card bookmark, other tab action) disappear instantly.
  const visible = useMemo(() => rows.filter((r) => isSaved(r.listing_id)), [rows, isSaved]);
  const available = visible.filter((r) => r.available && r.listing && !r.purchased);
  const owned = visible.filter((r) => r.purchased && r.listing);
  const gone = visible.filter((r) => !r.available && !r.purchased);

  if (!userId) return null;

  return (
    <section id="saved-leads" className="mb-8 scroll-mt-24">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-extrabold text-slate-900">
            <Bookmark className="h-5 w-5 fill-current text-orange-500" /> {es ? 'Leads guardados' : 'Saved leads'}
            <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-black text-orange-700">{count}</span>
          </h2>
          <p className="mt-0.5 text-sm text-slate-500">
            {es
              ? 'Guardar no desbloquea, compra ni reserva un lead — siguen las reglas normales de compra.'
              : 'Saving does not unlock, buy or reserve a lead — normal purchase rules still apply.'}
          </p>
        </div>
        <Link to={path('/jobs')} className="inline-flex min-h-[44px] items-center gap-1.5 text-sm font-bold text-orange-600">
          {es ? 'Buscar más leads' : 'Find more leads'} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {loading && rows.length === 0 ? (
        <div className="mt-4 flex items-center gap-2 rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> {es ? 'Cargando…' : 'Loading…'}
        </div>
      ) : error ? (
        <p className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          {es ? 'No se pudieron cargar sus leads guardados.' : 'Your saved leads could not be loaded.'}
        </p>
      ) : visible.length === 0 ? (
        <Link
          to={path('/jobs')}
          className="mt-4 flex min-h-[72px] flex-col items-center justify-center gap-1 rounded-2xl border-2 border-dashed border-slate-300 bg-white px-5 py-5 text-center text-sm text-slate-600 hover:border-orange-300"
        >
          <span className="inline-flex items-center gap-2 font-bold text-slate-800">
            <Bookmark className="h-4 w-4" /> {es ? 'Aún no ha guardado leads' : 'No saved leads yet'}
          </span>
          {es ? 'Toque el marcador en cualquier proyecto para verlo aquí después.' : 'Tap the bookmark on any project to keep it here for later.'}
        </Link>
      ) : (
        <>
          {available.length > 0 && (
            <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {available.slice(0, shown).map((r) => (
                <JobCard
                  key={r.listing_id}
                  job={r.listing!}
                  credits={credits}
                  onUnlock={(j) => navigate(path(`/jobs/${localizedSlug(j.slug, j.trade, lang)}`))}
                />
              ))}
            </div>
          )}
          {available.length > shown && (
            <button
              type="button"
              onClick={() => setShown((n) => n + PAGE)}
              className="mt-4 inline-flex min-h-[44px] w-full items-center justify-center rounded-xl border border-slate-300 bg-white text-sm font-bold text-slate-700 hover:bg-slate-50"
            >
              {es ? `Mostrar más (${available.length - shown})` : `Show more (${available.length - shown})`}
            </button>
          )}

          {(owned.length > 0 || gone.length > 0) && (
            <ul className="mt-4 divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white">
              {owned.map((r) => (
                <li key={r.listing_id} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
                  <Link
                    to={path(`/jobs/${localizedSlug(r.listing!.slug, r.listing!.trade, lang)}`)}
                    className="min-w-0 flex-1 truncate font-semibold text-slate-800 hover:text-orange-600"
                  >
                    {r.listing!.title}
                  </Link>
                  <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-700">
                    {es ? 'Ya comprado' : 'Already purchased'}
                  </span>
                  <RemoveBtn es={es} onClick={() => void toggle(r.listing_id).catch(() => undefined)} />
                </li>
              ))}
              {gone.map((r) => (
                <li key={r.listing_id} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm text-slate-500">
                  <Ban className="h-4 w-4 shrink-0 text-slate-400" />
                  <span className="min-w-0 flex-1 truncate">
                    {r.listing?.title || (es ? 'Proyecto retirado' : 'Project removed')}
                  </span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-600">
                    {es ? 'Ya no disponible' : 'No longer available'}
                  </span>
                  <RemoveBtn es={es} onClick={() => void toggle(r.listing_id).catch(() => undefined)} />
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
};

const RemoveBtn: React.FC<{ es: boolean; onClick: () => void }> = ({ es, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex min-h-[36px] items-center gap-1 rounded-lg border border-slate-200 px-2.5 text-xs font-bold text-slate-600 hover:border-red-200 hover:bg-red-50 hover:text-red-700"
  >
    <Trash2 className="h-3.5 w-3.5" /> {es ? 'Quitar' : 'Remove'}
  </button>
);

export default SavedLeadsPanel;
